import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, X, Check, Sparkles, PlusCircle, Trash2 } from "lucide-react";

/**
 * iter38 — Deliverables picker (gear-style checkbox popover).
 * iter42 — Persistent "Create & Re-use" custom presets.
 *
 *  Mirrors the visual language of `MultiSelectPills` (equipment inventory
 *  combobox) — search bar on top, grouped checkbox rows, multi-select stays
 *  open. Ships with the 24 signature Kahani presets (FF / Trailer / All Photos
 *  / Best Photos / Signature) organised into scannable buckets. When the
 *  admin types a phrase that doesn't match any preset, a "+ Create & Re-use:
 *  '<typed>'" button appears — clicking it (a) persists the term to
 *  localStorage so it becomes a first-class preset for every subsequent
 *  booking edit in this browser, AND (b) auto-toggles it into the current
 *  booking's deliverables array with its own checkbox row.
 */

const CUSTOM_PRESETS_LS_KEY = "kahani_custom_deliverable_presets_v1";

export const DELIVERABLE_PRESETS = [
  {
    category: "Full Function (FF) Videos",
    items: [
      "Bride Haldi FF",
      "Groom Haldi FF",
      "Bride Wedding FF",
      "Groom Wedding FF",
      "Bride Pooja FF",
      "Groom Pooja FF",
      "Bride Mehendi FF",
      "Groom Mehendi FF",
    ],
  },
  {
    category: "Trailer & Teaser",
    items: ["Trailer Video", "Teaser Video"],
  },
  {
    category: "All Photos",
    items: [
      "Bride Haldi All Photos",
      "Groom Haldi All Photos",
      "Bride Wedding All Photos",
      "Groom Wedding All Photos",
      "Bride Mehendi All Photos",
      "Groom Mehendi All Photos",
      "Bride Pooja All Photos",
      "Groom Pooja All Photos",
    ],
  },
  {
    category: "Best Photos",
    items: [
      "Wedding Best Photos",
      "Haldi Best Photos",
      "Mehendi Best Photos",
      "Pooja Best Photos",
    ],
  },
  {
    category: "Signature Deliverables",
    items: ["Premium Leather Album", "Traditional Video (Full Length)"],
  },
];

const STANDARD_PRESETS = new Set(
  DELIVERABLE_PRESETS.flatMap((g) => g.items).map((s) => s.toLowerCase())
);

// iter42 — Load / save persistent custom presets from localStorage.
function loadCustomPresets() {
  try {
    const raw = localStorage.getItem(CUSTOM_PRESETS_LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string" && s.trim()) : [];
  } catch {
    return [];
  }
}
function saveCustomPresets(list) {
  try {
    localStorage.setItem(CUSTOM_PRESETS_LS_KEY, JSON.stringify(list));
  } catch {
    /* quota / private-mode — fail silently */
  }
}

export default function DeliverablesPicker({ value = [], onChange, testid = "deliverables-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customPresets, setCustomPresets] = useState(() => loadCustomPresets());
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  // All groups (standard + persisted custom) that participate in search.
  const allGroups = useMemo(() => {
    if (!customPresets.length) return DELIVERABLE_PRESETS;
    return [
      ...DELIVERABLE_PRESETS,
      { category: "Your Custom Presets", items: customPresets, isCustom: true },
    ];
  }, [customPresets]);

  const knownAny = useCallback(
    (s) => {
      const low = s.trim().toLowerCase();
      if (STANDARD_PRESETS.has(low)) return true;
      return customPresets.some((c) => c.toLowerCase() === low);
    },
    [customPresets],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups
      .map((group) => {
        const catMatch = group.category.toLowerCase().includes(q);
        const items = catMatch ? group.items : group.items.filter((it) => it.toLowerCase().includes(q));
        return items.length ? { ...group, items } : null;
      })
      .filter(Boolean);
  }, [query, allGroups]);

  const totalMatches = filtered.reduce((s, g) => s + g.items.length, 0);

  const toggle = (item) => {
    if (value.includes(item)) onChange(value.filter((v) => v !== item));
    else onChange([...value, item]);
  };

  // iter42 — Promote the current query to a persistent custom preset AND
  // auto-tick its checkbox in the current booking. Used by both the new
  // "+ Create & Re-use" button and the Enter key.
  const createAndReuse = (raw) => {
    const s = (raw || "").trim();
    if (!s) return;
    if (!knownAny(s)) {
      const next = [...customPresets, s];
      setCustomPresets(next);
      saveCustomPresets(next);
    }
    if (!value.some((v) => v.toLowerCase() === s.toLowerCase())) {
      onChange([...value, s]);
    }
    setQuery("");
    searchRef.current?.focus();
  };

  const removeCustomPreset = (name) => {
    // Also uncheck it from the active deliverables — the admin removing a
    // library entry usually means they're regretting the whole choice.
    const next = customPresets.filter((c) => c !== name);
    setCustomPresets(next);
    saveCustomPresets(next);
    if (value.includes(name)) onChange(value.filter((v) => v !== name));
  };

  const onSearchKey = (e) => {
    if (e.key === "Escape") { e.stopPropagation(); setOpen(false); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      createAndReuse(query);
    }
  };

  const trimmed = query.trim();
  const showCreateCTA = !!trimmed && !knownAny(trimmed);

  return (
    <div ref={rootRef} className="relative inline-block" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-rose-700 font-semibold hover:bg-rose-50 rounded px-2 py-0.5"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
      >
        <Plus className="w-3 h-3" strokeWidth={2.5} />
        Add item
      </button>

      {open && (
        <div
          className="absolute z-30 top-full right-0 mt-1 w-80 bg-white border border-stone-200 rounded-lg shadow-xl overflow-hidden flex flex-col"
          onMouseDown={(e) => e.stopPropagation()}
          data-testid={`${testid}-menu`}
        >
          {/* Search bar */}
          <div className="p-2 border-b border-stone-100 bg-stone-50">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={2} />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Type to search… (Enter = create & re-use)"
                className="w-full pl-7 pr-6 py-1.5 text-xs border border-stone-200 rounded bg-white focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200"
                data-testid={`${testid}-search`}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(""); searchRef.current?.focus(); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-200 text-stone-500"
                  aria-label="Clear search"
                  data-testid={`${testid}-search-clear`}
                >
                  <X className="w-3 h-3" strokeWidth={2.4} />
                </button>
              )}
            </div>
          </div>

          {/* Grouped preset checkboxes */}
          <div className="max-h-72 overflow-y-auto p-2 space-y-3">
            {filtered.map((group) => (
              <div key={group.category}>
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold px-1 pb-1 flex items-center gap-1">
                  {group.isCustom && <Sparkles className="w-3 h-3 text-rose-500" strokeWidth={2.4} />}
                  {group.category}
                  {group.isCustom && (
                    <span className="ml-auto text-[9px] text-stone-400 normal-case tracking-normal italic">saved in this browser</span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-0.5">
                  {group.items.map((item) => {
                    const on = value.includes(item);
                    return (
                      <div key={item} className="flex items-center gap-1 group">
                        <button
                          type="button"
                          onClick={() => toggle(item)}
                          className={`flex-1 text-xs flex items-center gap-2 px-2 py-1 rounded hover:bg-stone-50 text-left ${
                            on ? "text-rose-800 font-semibold" : "text-stone-700"
                          }`}
                          data-testid={`${testid}-option-${item}`}
                        >
                          <span
                            className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                              on ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-stone-300"
                            }`}
                          >
                            {on && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                          </span>
                          <HighlightedText text={item} query={query} />
                        </button>
                        {group.isCustom && (
                          <button
                            type="button"
                            onClick={() => removeCustomPreset(item)}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-700 transition"
                            title={`Remove "${item}" from saved presets`}
                            aria-label={`Remove ${item} from saved presets`}
                            data-testid={`${testid}-remove-custom-${item}`}
                          >
                            <Trash2 className="w-3 h-3" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {trimmed && totalMatches === 0 && !showCreateCTA && (
              <div
                className="text-xs text-stone-500 px-2 py-3 text-center border border-dashed border-stone-200 rounded"
                data-testid={`${testid}-no-matches`}
              >
                No presets match &ldquo;{trimmed}&rdquo;.
              </div>
            )}
          </div>

          {/* iter42 — "Create & Re-use" button appears whenever the query is
              a non-empty string that isn't already a standard or custom preset.
              This works even when partial matches exist above so the admin can
              still spin up a brand-new library entry. */}
          {showCreateCTA && (
            <div className="border-t border-stone-100 p-2 bg-rose-50/60">
              <button
                type="button"
                onClick={() => createAndReuse(query)}
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded px-2 py-1.5 transition shadow-sm"
                data-testid={`${testid}-add-custom`}
              >
                <PlusCircle className="w-3.5 h-3.5" strokeWidth={2.4} />
                Create &amp; Re-use: &ldquo;{trimmed}&rdquo;
              </button>
              <div className="text-[9px] text-stone-500 italic text-center mt-1">
                Saved to your custom presets library for future bookings.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HighlightedText({ text, query }) {
  const q = (query || "").trim();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-100 text-amber-900 rounded-sm px-0.5 py-0">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}
